import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bookmark, Plus, ExternalLink, CheckCircle, RefreshCw, Trash2, BarChart2 } from 'lucide-react';
import { api } from '../../context/AuthContext';

const CATEGORY_COLOR = {
  community:   'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
  tech:        'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  read_later:  'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
  bookmarking: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  discovery:   'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  news:        'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
  curation:    'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
  academic:    'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  magazine:    'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
  tech_news:   'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
};

const errDetail = (err) => {
  const d = err.response?.data?.detail;
  return typeof d === 'string' ? d : err.message || 'Request failed';
};

export default function SocialBookmarkingPage() {
  const [sites, setSites] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('create');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form
  const [pageUrl, setPageUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [selectedSites, setSelectedSites] = useState([]);
  const [selectAll, setSelectAll] = useState(true);

  // Active campaign view
  const [activeCampaign, setActiveCampaign] = useState(null);

  const load = async () => {
    try {
      const [sitesRes, campRes, statsRes] = await Promise.all([
        api.get('/social-bookmarking/sites'),
        api.get('/social-bookmarking/campaigns'),
        api.get('/social-bookmarking/stats'),
      ]);
      setSites(sitesRes.data.sites || []);
      setCampaigns(campRes.data.campaigns || []);
      setStats(statsRes.data);
    } catch { /* silent */ }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (selectAll) setSelectedSites([]);
  }, [selectAll]);

  const toggleSite = (id) => {
    setSelectAll(false);
    setSelectedSites((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!pageUrl.trim() || !title.trim()) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/social-bookmarking/campaigns', {
        page_url: pageUrl.trim(),
        title: title.trim(),
        description: description.trim(),
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
        selected_sites: selectAll ? null : selectedSites,
      });
      if (data.success) {
        setActiveCampaign(data.campaign);
        setActiveTab('campaign');
        await load();
        setPageUrl('');
        setTitle('');
        setDescription('');
        setTags('');
        setSelectedSites([]);
        setSelectAll(true);
      } else {
        setError(data.error || 'Failed to create campaign');
      }
    } catch (err) {
      setError(errDetail(err));
    } finally {
      setLoading(false);
    }
  };

  const markSubmitted = async (campaignId, siteId) => {
    try {
      const { data } = await api.patch(`/social-bookmarking/campaigns/${campaignId}/status`, {
        site_id: siteId,
        status: 'submitted',
      });
      if (data.success) {
        setActiveCampaign(data.campaign);
        setCampaigns((prev) => prev.map((c) => (c.id === campaignId ? data.campaign : c)));
      }
    } catch { /* silent */ }
  };

  return (
    <motion.div
      className="space-y-6 p-4 md:p-6"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
          <Bookmark className="w-5 h-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Social Bookmarking</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-0.5 text-sm">
            Generate one-click submission links for Reddit, Pocket, Digg, Hacker News, and more.
          </p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Campaigns', value: stats.total_campaigns },
            { label: 'Total Sites', value: stats.total_sites },
            { label: 'Submitted', value: stats.total_submitted },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 text-center shadow-sm">
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{s.value}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 dark:border-slate-800">
        {[
          { id: 'create', label: 'Create Campaign' },
          { id: 'campaign', label: 'Submission Links', disabled: !activeCampaign },
          { id: 'history', label: `History (${campaigns.length})` },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => !tab.disabled && setActiveTab(tab.id)}
            disabled={tab.disabled}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 disabled:opacity-40'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Create */}
      {activeTab === 'create' && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm space-y-4">
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Page URL *</label>
                <input
                  type="url"
                  value={pageUrl}
                  onChange={(e) => setPageUrl(e.target.value)}
                  placeholder="https://yoursite.com/page"
                  required
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Title *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Page title for bookmarking"
                  required
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Description</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Short description"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Tags (comma-separated)</label>
                <input
                  type="text"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="seo, marketing, tips"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
            </div>

            {/* Site selection */}
            <div>
              <div className="flex items-center gap-3 mb-2">
                <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Bookmarking Sites</label>
                <button
                  type="button"
                  onClick={() => { setSelectAll(!selectAll); if (!selectAll) setSelectedSites([]); }}
                  className="text-xs text-amber-600 dark:text-amber-400 hover:underline"
                >
                  {selectAll ? 'Select specific' : 'Select all'}
                </button>
              </div>
              {!selectAll && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                  {sites.map((site) => (
                    <button
                      key={site.id}
                      type="button"
                      onClick={() => toggleSite(site.id)}
                      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md border text-xs transition-colors ${
                        selectedSites.includes(site.id)
                          ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300'
                          : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-amber-300'
                      }`}
                    >
                      {selectedSites.includes(site.id) && <CheckCircle className="w-3 h-3" />}
                      {site.name}
                      <span className="ml-auto text-slate-400 text-[10px]">DA{site.da}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 bg-amber-600 text-white px-5 py-2 rounded-md text-sm hover:bg-amber-700 disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              {loading ? 'Generating...' : 'Generate Submission Links'}
            </button>
          </form>
        </div>
      )}

      {/* Campaign / submission links */}
      {activeTab === 'campaign' && activeCampaign && (
        <div className="space-y-3">
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 shadow-sm">
            <p className="font-semibold text-slate-900 dark:text-white">{activeCampaign.title}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{activeCampaign.page_url}</p>
            <div className="mt-2 flex gap-3 text-xs text-slate-500">
              <span>{activeCampaign.total_sites} sites</span>
              <span>{activeCampaign.submitted_count} submitted</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(activeCampaign.bookmarks || []).map((bm) => (
              <div
                key={bm.site_id}
                className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-3 flex items-center justify-between gap-3 shadow-sm"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-slate-900 dark:text-white">{bm.site_name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${CATEGORY_COLOR[bm.category] || 'bg-slate-100 text-slate-600'}`}>
                      DA{bm.da}
                    </span>
                  </div>
                  {bm.status === 'submitted' && (
                    <span className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1 mt-0.5">
                      <CheckCircle className="w-3 h-3" /> Submitted
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <a
                    href={bm.submit_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-amber-600 text-white hover:bg-amber-700"
                  >
                    <ExternalLink className="w-3 h-3" /> Submit
                  </a>
                  {bm.status !== 'submitted' && (
                    <button
                      onClick={() => markSubmitted(activeCampaign.id, bm.site_id)}
                      className="text-xs px-2.5 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-900"
                    >
                      Mark done
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* History */}
      {activeTab === 'history' && (
        <div className="space-y-3">
          {campaigns.length === 0 ? (
            <p className="text-center text-slate-500 dark:text-slate-400 py-12 text-sm">No campaigns yet.</p>
          ) : (
            campaigns.map((c) => (
              <div
                key={c.id}
                className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 shadow-sm flex items-center justify-between gap-4 cursor-pointer hover:border-amber-300 dark:hover:border-amber-700 transition-colors"
                onClick={() => { setActiveCampaign(c); setActiveTab('campaign'); }}
              >
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">{c.title}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{c.page_url}</p>
                </div>
                <div className="text-right text-xs text-slate-500 shrink-0">
                  <p>{c.submitted_count}/{c.total_sites} submitted</p>
                  <p>{new Date(c.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </motion.div>
  );
}
